import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { GlobeData, GlobeFilters } from '../types/globe.types';

interface UseGlobeDataOptions {
  programId: string | null;
  teamId: string | null;
  filters: GlobeFilters;
  enabled?: boolean;
}

interface ContactFeedItem {
  id: string;
  description: string;
  status: string;
  severity: string;
  created_at: string;
  updated_at: string;
  reporter_email: string | null;
  reporter_name: string | null;
  user_metadata: Record<string, unknown> | null;
  user_properties: Record<string, unknown> | null;
  internal_notes: string | null;
}

interface ContactFeedResponse {
  contacts: ContactFeedItem[];
}

function getDateRange(filters: GlobeFilters) {
  if (filters.customDateRange) {
    return {
      from: filters.customDateRange.start,
      to: filters.customDateRange.end,
    };
  }

  const now = new Date();
  switch (filters.timeRange) {
    case '24h':
      return { from: new Date(now.getTime() - 24 * 60 * 60 * 1000), to: now };
    case '7d':
      return { from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), to: now };
    case '30d':
      return { from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000), to: now };
    case 'custom':
      return { from: undefined, to: undefined };
    case 'all':
    default:
      return { from: undefined, to: undefined };
  }
}

export function useGlobeData({ programId, teamId, filters, enabled = true }: UseGlobeDataOptions) {
  return useQuery<GlobeData>({
    queryKey: ['globe-data', programId, teamId, filters],
    queryFn: async () => {
      if (!programId || !teamId) {
        return {
          submissions: [],
          stats: { total: 0, countries: 0, today: 0, liveNow: 0 },
          trending: [],
          distribution: { bug: 0, feature: 0, response: 0, contact: 0, praise: 0, review: 0, other: 0 },
        };
      }

      const params = new URLSearchParams({
        programId,
        teamId,
        timeRange: filters.timeRange,
        types: filters.types.join(','),
      });

      if (filters.customDateRange) {
        params.append('startDate', filters.customDateRange.start.toISOString());
        params.append('endDate', filters.customDateRange.end.toISOString());
      }

      const includeContacts = filters.types.includes('contact');
      const { from, to } = getDateRange(filters);

      const [response, contactResponse] = await Promise.all([
        apiClient<GlobeData>(`/api/locations/map-data?${params.toString()}`),
        includeContacts
          ? (() => {
              const contactParams = new URLSearchParams({
                programId,
                page: '1',
                limit: '50',
                sort: 'created_at_desc',
              });

              if (from) {
                contactParams.append('fromDate', from.toISOString());
              }
              if (to) {
                contactParams.append('toDate', to.toISOString());
              }

              return apiClient<ContactFeedResponse>(
                `/api/contacts/protected?${contactParams.toString()}`
              );
            })()
          : Promise.resolve({ contacts: [] }),
      ]);

      const contactSubmissions = (contactResponse.contacts || []).map((contact) => ({
        id: contact.id,
        type: 'contact' as const,
        title:
          contact.reporter_name ||
          contact.reporter_email ||
          'Mailbox Message',
        description: contact.description,
        location: {
          lat: null,
          lng: null,
          city: '',
          country: '',
          countryCode: '',
          hasCoordinates: false,
        },
        timestamp: contact.created_at,
        status: contact.status || 'new',
        severity: contact.severity,
        userName: contact.reporter_name || undefined,
        userEmail: contact.reporter_email || undefined,
        userMetadata: contact.user_metadata || undefined,
        customFields: contact.user_properties || undefined,
        internalNotes: contact.internal_notes || '',
      }));

      const submissions = [...(response.submissions || []), ...contactSubmissions].sort(
        (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const contactTodayCount = contactSubmissions.filter(
        (contact) => new Date(contact.timestamp).getTime() >= todayStart.getTime()
      ).length;

      return {
        ...response,
        submissions,
        stats: {
          ...response.stats,
          total: (response.stats?.total || 0) + contactSubmissions.length,
          today: (response.stats?.today || 0) + contactTodayCount,
        },
        distribution: {
          ...response.distribution,
          contact: contactSubmissions.length,
        },
      };
    },
    enabled: enabled && !!programId && !!teamId,
    // FIXED: Adaptive refetch interval based on recent activity
    refetchInterval: (data) => {
      if (!data) return 60000; // Default to 1 minute if no data

      // Check if there are submissions in the last 5 minutes
      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      const hasRecentSubmissions = data.submissions?.some(
        (submission) => new Date(submission.timestamp).getTime() > fiveMinutesAgo
      );

      // If there's recent activity, refetch every minute
      // Otherwise, refetch every 5 minutes to save bandwidth and reduce server load
      return hasRecentSubmissions ? 60000 : 300000; // 1 min vs 5 min
    },
    staleTime: 60000, // Consider data stale after 60 seconds (improved from 10s)
    gcTime: 300000, // Keep unused data in cache for 5 minutes (renamed from cacheTime)
  });
}
